/**
 * ExportPdfTrigger — dashboard-level "Export to PDF" button.
 *
 * Captures the dashboard widget stack (passed via `targetRef`) into a
 * GoodData-style PDF: portrait A4, no Neeve branding, title + date in the
 * header, "Page X/Y" footer. See `frontend/src/utils/analyticsPdf.ts` and
 * the reference PDFs in `dev-samples/mvp/GoodData Sample Reports/`.
 */

import { useCallback, useState, type RefObject } from 'react';
import { Button } from '@/components/ui/button';
import { Download } from '@/utils/icons';
import { exportAnalyticsPdf } from '@/utils/analyticsPdf';

interface ExportPdfTriggerProps {
  /** Dashboard title — rendered top-left of every PDF page. */
  title: string;
  /** Filename slug (no extension; date is appended automatically). */
  filenameSlug: string;
  /** Ref to the DOM element wrapping the dashboard widget stack to capture. */
  targetRef: RefObject<HTMLElement>;
  /** Render as a small icon button instead of a labeled button. */
  iconOnly?: boolean;
  /** Optional extra class names. */
  className?: string;
}

export function ExportPdfTrigger({
  title,
  filenameSlug,
  targetRef,
  iconOnly,
  className,
}: ExportPdfTriggerProps) {
  const [busy, setBusy] = useState(false);

  const handleClick = useCallback(async () => {
    if (busy) return;
    const target = targetRef.current;
    if (!target) {
      console.error('ExportPdfTrigger: targetRef is empty — nothing to export');
      return;
    }
    setBusy(true);
    try {
      await exportAnalyticsPdf({ title, filenameSlug, target });
    } catch (err) {
      console.error('PDF export failed', err);
      window.alert('PDF export failed. See console for details.');
    } finally {
      setBusy(false);
    }
  }, [busy, title, filenameSlug, targetRef]);

  if (iconOnly) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className={className}
        onClick={handleClick}
        disabled={busy}
        aria-label="Export dashboard to PDF"
        title="Export dashboard to PDF"
        data-pdf-exclude="true"
      >
        <Download className="dls-h-4 dls-w-4" />
      </Button>
    );
  }

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={handleClick}
      disabled={busy}
      className={className}
      data-pdf-exclude="true"
    >
      <Download className="dls-h-3.5 dls-w-3.5 dls-mr-1.5" />
      {busy ? 'Exporting…' : 'Export to PDF'}
    </Button>
  );
}
